import { useMemo, useState } from "react";
import { RowSizes } from "~/components/DataGrid";
import { Link } from "~/components/Link";
import { Emdash } from "~/components/Punctuation";
import { SortInput } from "~/components/SortInput";
import { cn } from "~/lib/utils";
import { SortDirections } from "~/modules/filters/types";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import {
    type PerformanceByPeriod,
    type PerformanceGroup,
    type PerformancePeriod,
} from "~/modules/asset_classes/performance-types";
import { getReturnRows, type ReturnRow } from "./getReturnRows";

// // // //

enum ReturnColumns {
    name = "name",
    oneMonth = "oneMonth",
    threeMonth = "threeMonth",
    yearToDate = "yearToDate",
    oneYear = "oneYear",
}

/**
 * RETURN_PERIOD_COLUMNS
 * All four periods are always visible and sort on their own, decoupled from the
 * period that scales the chart.
 */
type ReturnPeriodColumn =
    | ReturnColumns.oneMonth
    | ReturnColumns.threeMonth
    | ReturnColumns.yearToDate
    | ReturnColumns.oneYear;

const RETURN_PERIOD_COLUMNS: Array<{
    field: ReturnPeriodColumn;
    label: string;
}> = [
    { field: ReturnColumns.oneMonth, label: "1M" },
    { field: ReturnColumns.threeMonth, label: "3M" },
    { field: ReturnColumns.yearToDate, label: "YTD" },
    { field: ReturnColumns.oneYear, label: "1Y" },
];

// NOTE - shared by the header row and every data row: name, one column per period
const LEADERBOARD_GRID_CLASS_NAME =
    "grid grid-cols-[minmax(180px,1fr)_repeat(4,92px)] items-center";

const ROW_HEIGHT = 30;

function ReturnValue(props: { isHidden: boolean; value: number | null }) {
    const { isHidden, value } = props;

    if (value === null) {
        return (
            <div
                className={cn(
                    "flex items-center justify-end px-2 text-sm text-muted-foreground",
                    { "opacity-35": isHidden },
                )}
            >
                <Emdash />
            </div>
        );
    }

    return (
        <div
            className={cn(
                "flex items-center justify-end px-2 text-sm whitespace-nowrap tabular-nums",
                {
                    "text-foreground/80": value === 0,
                    "text-change-up": value > 0,
                    "text-change-down": value < 0,
                    "opacity-35": isHidden,
                },
            )}
        >
            {value !== 0 && (
                <span aria-hidden="true" className="mr-0.5 text-[10px]/none">
                    {value > 0 ? "▲" : "▼"}
                </span>
            )}
            {value === 0 ? "0%" : `${formatPercentValue(Math.abs(value))}%`}
        </div>
    );
}

interface LeaderboardSort {
    direction: SortDirections.asc | SortDirections.desc;
    field: ReturnColumns;
}

const DEFAULT_SORT: LeaderboardSort = {
    direction: SortDirections.desc,
    field: ReturnColumns.oneYear,
};

function LeaderboardHeaderCell(props: {
    align: "left" | "right";
    field: ReturnColumns;
    label: string;
    onChangeSort: (field: ReturnColumns) => void;
    sort: LeaderboardSort;
}) {
    const { align, field, label, onChangeSort, sort } = props;
    const isActive = sort.field === field;

    return (
        <button
            type="button"
            onClick={() => onChangeSort(field)}
            className={cn(
                "group flex min-w-0 cursor-pointer items-center gap-1.5 transition-colors hover:text-slate-800",
                align === "right" ? "justify-end px-2" : "justify-start px-4",
                { "font-medium text-foreground": isActive },
            )}
        >
            <span className="overflow-hidden text-ellipsis whitespace-nowrap select-none">
                {label}
            </span>
            <SortInput
                direction={isActive ? sort.direction : SortDirections.none}
                size={RowSizes.extraSmall}
            />
        </button>
    );
}

function LeaderboardRow(props: {
    isHidden: boolean;
    isHighlighted: boolean;
    isStriped: boolean;
    onHighlightedSeriesChange: (seriesID: number | null) => void;
    onToggleSeries: (seriesID: number) => void;
    row: ReturnRow;
}) {
    const {
        isHidden,
        isHighlighted,
        isStriped,
        onHighlightedSeriesChange,
        onToggleSeries,
        row,
    } = props;
    const nameClassName = cn(
        "overflow-hidden text-sm text-ellipsis whitespace-nowrap",
        { "text-muted-foreground line-through": isHidden },
    );

    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={!isHidden}
            aria-label={`${isHidden ? "Show" : "Hide"} ${row.name} in the chart`}
            style={{ height: ROW_HEIGHT }}
            className={cn(
                LEADERBOARD_GRID_CLASS_NAME,
                "cursor-pointer transition-colors duration-150 outline-none",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                {
                    // NOTE - --blue-50 has no dark counterpart, so fall back to
                    // the theme-aware active row color there
                    "bg-blue-50 dark:bg-grid-row-active": isHighlighted,
                    "bg-grid-row": !isHighlighted && !isStriped,
                    "bg-grid-row-striped": !isHighlighted && isStriped,
                },
            )}
            onClick={() => onToggleSeries(row.id)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onToggleSeries(row.id);
                }
            }}
            onMouseEnter={() => onHighlightedSeriesChange(row.id)}
            onMouseLeave={() => onHighlightedSeriesChange(null)}
            onFocus={() => onHighlightedSeriesChange(row.id)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    onHighlightedSeriesChange(null);
                }
            }}
        >
            <div className="flex min-w-0 items-center gap-2 px-4">
                <span
                    aria-hidden="true"
                    className="size-2.5 shrink-0 rounded-full"
                    style={
                        isHidden
                            ? {
                                  boxShadow: `inset 0 0 0 1.5px ${row.color}`,
                                  opacity: 0.5,
                              }
                            : { backgroundColor: row.color }
                    }
                />
                {row.href === null || row.analyticsLabel === null ? (
                    <span className={nameClassName}>{row.name}</span>
                ) : (
                    <Link
                        href={row.href}
                        analyticsLabel={row.analyticsLabel}
                        variant={isHidden ? "unstyled" : "default"}
                        stopPropagation
                        className={nameClassName}
                    >
                        {row.name}
                    </Link>
                )}
            </div>

            {RETURN_PERIOD_COLUMNS.map((column) => (
                <ReturnValue
                    key={column.field}
                    isHidden={isHidden}
                    value={row[column.field]}
                />
            ))}
        </div>
    );
}

interface AssetPerformanceLeaderboardProps {
    className?: string;
    group: PerformanceGroup;
    hiddenSeriesIDs: Record<number, boolean>;
    highlightedSeriesID: number | null;
    onHighlightedSeriesChange: (seriesID: number | null) => void;
    onToggleSeries: (seriesID: number) => void;
    performanceByPeriod: PerformanceByPeriod;
    performanceManagerSlugs?: Record<number, string>;
    period: PerformancePeriod;
}

function useLeaderboardRows(
    props: Pick<
        AssetPerformanceLeaderboardProps,
        "group" | "performanceByPeriod" | "performanceManagerSlugs" | "period"
    >,
) {
    const { group, performanceByPeriod, performanceManagerSlugs, period } =
        props;

    return useMemo(
        () =>
            getReturnRows(
                performanceByPeriod,
                period,
                group,
                performanceManagerSlugs,
            ),
        [group, performanceByPeriod, performanceManagerSlugs, period],
    );
}

/**
 * AssetPerformanceLeaderboard
 * Ranked rail that replaces the chart legend: hovering a row highlights the
 * matching series, clicking a row hides or shows it, and every column sorts on
 * its own — independent of the period driving the chart.
 */
export function AssetPerformanceLeaderboard(
    props: AssetPerformanceLeaderboardProps,
) {
    const {
        className,
        group,
        hiddenSeriesIDs,
        highlightedSeriesID,
        onHighlightedSeriesChange,
        onToggleSeries,
    } = props;
    const [sort, setSort] = useState<LeaderboardSort>(DEFAULT_SORT);
    const rows = useLeaderboardRows(props);
    const groupLabel = group === "assetClass" ? "Asset Class" : "Manager";
    const sortedRows = useMemo(
        () =>
            sortItemsByProperty({
                items: [...rows],
                field: sort.field,
                direction: sort.direction,
                nullsLast: true,
                caseSensitive: false,
            }),
        [rows, sort.direction, sort.field],
    );

    function changeSort(field: ReturnColumns) {
        setSort((current) => ({
            direction:
                current.field === field &&
                current.direction === SortDirections.desc
                    ? SortDirections.asc
                    : SortDirections.desc,
            field,
        }));
    }

    if (rows.length === 0) {
        return null;
    }

    return (
        <div className={cn("flex min-w-0 flex-col overflow-x-auto", className)}>
            <div className="flex min-w-[540px] grow flex-col">
                <div
                    className={cn(
                        LEADERBOARD_GRID_CLASS_NAME,
                        "border-b border-border/60 bg-muted py-1.5 text-xs text-slate-600",
                    )}
                >
                    <LeaderboardHeaderCell
                        align="left"
                        field={ReturnColumns.name}
                        label={groupLabel}
                        sort={sort}
                        onChangeSort={changeSort}
                    />
                    {RETURN_PERIOD_COLUMNS.map((column) => (
                        <LeaderboardHeaderCell
                            key={column.field}
                            align="right"
                            field={column.field}
                            label={column.label}
                            sort={sort}
                            onChangeSort={changeSort}
                        />
                    ))}
                </div>

                {sortedRows.map((row, index) => (
                    <LeaderboardRow
                        key={row.id}
                        isHidden={!!hiddenSeriesIDs[row.id]}
                        isHighlighted={highlightedSeriesID === row.id}
                        isStriped={index % 2 === 1}
                        onHighlightedSeriesChange={onHighlightedSeriesChange}
                        onToggleSeries={onToggleSeries}
                        row={row}
                    />
                ))}

                {/* NOTE - continues the zebra striping through any leftover height */}
                <div
                    aria-hidden="true"
                    className="min-h-0 grow"
                    style={{
                        background: `repeating-linear-gradient(180deg, ${
                            sortedRows.length % 2 === 0
                                ? `var(--grid-row) 0 ${ROW_HEIGHT}px, var(--grid-row-striped) ${ROW_HEIGHT}px ${ROW_HEIGHT * 2}px`
                                : `var(--grid-row-striped) 0 ${ROW_HEIGHT}px, var(--grid-row) ${ROW_HEIGHT}px ${ROW_HEIGHT * 2}px`
                        })`,
                    }}
                />
            </div>
        </div>
    );
}
